import { VStack, HStack, Button, Box, Text, Flex } from '@chakra-ui/react';
import { usePagination } from '../../contexts/PaginationContext';

interface PaginatedListProps<T> {
  renderItem: (item: T) => React.ReactNode;
}

const PaginatedList = <T extends { id: string }>({
  renderItem,
}: PaginatedListProps<T>) => {
  const { paginatedData, currentPage, setCurrentPage, totalPages } =
    usePagination<T>();

  const items = paginatedData.items;

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    const delta = 1;

    pages.push(1);

    if (currentPage - delta > 2) {
      pages.push('...');
    }

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }

    if (currentPage + delta < totalPages - 1) {
      pages.push('...');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages.map((page, idx) =>
        typeof page === 'number' ? (
        <Button
          key={idx}
          onClick={() => setCurrentPage(page)}
          size="lg"
          borderRadius="3xl"
          fontWeight="bold"
          boxShadow="sm"
          color={page === currentPage ? 'white' : 'primary.500'}
          bg={page === currentPage ? 'primary.500' : 'white'}
          border={page === currentPage ? 'none' : '1px solid'}
          borderColor={page === currentPage ? undefined : 'surface.300'}
          _hover={{
            bg: page === currentPage ? 'primary.500' : 'surface.200',
            transform: 'scale(1.05)',
          }}
        >
          {page}
        </Button>
      ) : (
        <Text key={`ellipsis-${idx}`} px={2}>
          ...
        </Text>
      )
    );
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* height container */}
      <Box minHeight="300px">
        {items.map((item) => (
          <Box key={item.id} mb={6}>
            {renderItem(item)}
          </Box>
        ))}
      </Box>

      {totalPages > 1 && (
        <Flex justify="center" mt={0} minHeight="60px">
          <HStack spacing={2}>
            <Button
              onClick={handlePrev}
              isDisabled={currentPage === 1}
              size="lg"
              bg="surface.300"
              color="primary.500"
              borderRadius="2xl"
              boxShadow="sm"
              _hover={{ bg: 'surface.400' }}
            >
              Previous
            </Button>

            {renderPageNumbers()}

            <Button
              onClick={handleNext}
              isDisabled={currentPage === totalPages}
              size="lg"
              fontSize="0.5xl"
              bg="surface.300"
              color="primary.500"
              borderRadius="2xl"
              boxShadow="sm"
              _hover={{ bg: 'surface.400' }}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}
    </VStack>
  );
};

export default PaginatedList;
